import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../../lib/cn';

/**
 * Multi-pane backbone: a `main` column plus an optional sticky `aside` rail.
 *
 * MOBILE IS A NO-OP. Below `md` the wrapper and both column boxes are
 * `display: contents`, so they generate no box at all — `main` and `aside` children
 * render exactly as if they were inline siblings in the parent, preserving the
 * existing single-column DOM order byte for byte.
 *
 * Tablet (`md`–`lg`, 768–1023px): side-by-side with a **narrow** rail
 * (`asideWidthMd`, default 260px) beside the slim icon nav.
 * Desktop (`lg+`, 1024px+): full-width rail (`asideWidth`, default 360px).
 */
export function DesktopColumns({
  main,
  aside,
  asideWidth = '360px',
  asideWidthMd = '260px',
  gap = 'md:gap-4 lg:gap-6',
  stickyAside = true,
  asidePosition = 'right',
  railFirst = false,
  className,
  mainClassName,
  asideClassName
}: {
  main: ReactNode;
  /** Optional context rail. When omitted, this renders `main` with zero wrapper cost. */
  aside?: ReactNode;
  /** Rail width at `lg+` (any CSS length). */
  asideWidth?: string;
  /** Rail width in the tablet band (`md`–`lg`). Defaults to 260px. */
  asideWidthMd?: string;
  /** Column gap utility (so callers can widen it at `xl`). */
  gap?: string;
  /** Pin the rail while the main column scrolls (tablet + desktop). */
  stickyAside?: boolean;
  /** Which side the rail sits on from `md` up. */
  asidePosition?: 'left' | 'right';
  /**
   * Emit the rail BEFORE the main column in the DOM. Use when the rail's content
   * sits ABOVE the main content on mobile today (e.g. a section nav / summary header
   * over a list) so the single-column `< md` order is preserved. Visual side from
   * `md` is still set by {@link asidePosition}.
   */
  railFirst?: boolean;
  className?: string;
  mainClassName?: string;
  asideClassName?: string;
}) {
  const cols =
    asidePosition === 'left'
      ? 'md:[grid-template-columns:var(--h2h-aside-md)_minmax(0,1fr)] lg:[grid-template-columns:var(--h2h-aside)_minmax(0,1fr)]'
      : 'md:[grid-template-columns:minmax(0,1fr)_var(--h2h-aside-md)] lg:[grid-template-columns:minmax(0,1fr)_var(--h2h-aside)]';

  const mainNode = (
    <div
      key="main"
      className={cn(
        'contents md:block md:min-w-0',
        asidePosition === 'left' ? 'md:order-2' : 'md:order-1',
        mainClassName
      )}
    >
      {main}
    </div>
  );

  const asideNode =
    aside != null ? (
      <aside
        key="aside"
        className={cn(
          'contents md:block md:min-w-0',
          asidePosition === 'left' ? 'md:order-1' : 'md:order-2',
          stickyAside && 'md:sticky md:top-4 md:self-start',
          asideClassName
        )}
      >
        {aside}
      </aside>
    ) : null;

  return (
    <div
      className={cn('contents md:grid md:items-start', cols, gap, className)}
      style={
        { '--h2h-aside-md': asideWidthMd, '--h2h-aside': asideWidth } as CSSProperties
      }
    >
      {railFirst ? [asideNode, mainNode] : [mainNode, asideNode]}
    </div>
  );
}
